export type AIStageKey =
  | 'prosecutor'
  | 'defender'
  | 'supervisor'
  | 'senior_review'
  | 'mva'
  | 'mva_escalation'
  | 'contact_compliance';

export const AI_COST_RATE_DATE = '2026-08-17';

export const AI_STAGE_LABELS: Record<AIStageKey, string> = {
  prosecutor: 'Prosecutor',
  defender: 'Defender',
  supervisor: 'Supervisor',
  senior_review: 'Senior review',
  mva: 'MVA qualification',
  mva_escalation: 'MVA escalation',
  contact_compliance: 'Contact compliance',
};

type AICallLike = {
  Date?: string | null;
  qa_scored?: boolean | null;
  prosecutor_case?: string | null;
  prosecutor_would_rule?: string | null;
  defender_case?: string | null;
  defender_would_rule?: string | null;
  supervisor_disposition?: string | null;
  sr_escalated?: boolean | null;
  sr_disposition?: string | null;
  court_tier_reached?: string | null;
  mva_qualified?: string | null;
  mva_evidence?: string | null;
  mva_escalated?: boolean | null;
  contact_compliance_assessment_status?: string | null;
  contact_compliance_scored_at?: string | null;
  prosecutor_model?: string | null;
  defender_model?: string | null;
  supervisor_model?: string | null;
  sr_model?: string | null;
  mva_model?: string | null;
  contact_compliance_model?: string | null;
};

type ModelRate = {
  inputPerMillionUsd: number;
  outputPerMillionUsd: number;
};

const MODEL_RATES: Array<{ match: RegExp; rate: ModelRate }> = [
  {
    match: /claude-sonnet-5/i,
    rate: { inputPerMillionUsd: 2, outputPerMillionUsd: 10 },
  },
  {
    match: /claude-opus-5/i,
    rate: { inputPerMillionUsd: 5, outputPerMillionUsd: 25 },
  },
  {
    match: /claude-haiku-4\.5/i,
    rate: { inputPerMillionUsd: 1, outputPerMillionUsd: 5 },
  },
  {
    match: /gpt-5\.6-luna/i,
    rate: { inputPerMillionUsd: 0.1, outputPerMillionUsd: 0.6 },
  },
];

// Representative token profiles sampled from successful production executions.
// These support operational estimates only; they are not provider billing records.
const STAGE_TOKEN_PROFILE: Record<AIStageKey, { input: number; output: number }> = {
  prosecutor: { input: 4644, output: 267 },
  defender: { input: 2205, output: 145 },
  supervisor: { input: 4204, output: 349 },
  senior_review: { input: 6200, output: 600 },
  mva: { input: 6735, output: 2700 },
  mva_escalation: { input: 6941, output: 2264 },
  contact_compliance: { input: 8210, output: 569 },
};

export interface AIRunEstimate {
  stage: AIStageKey;
  label: string;
  model: string | null;
  modelLabel: string;
  modelInferred: boolean;
  sampledInputTokens: number;
  sampledOutputTokens: number;
  estimatedCostUsd: number | null;
}

export interface AICallCostEstimate {
  runs: AIRunEstimate[];
  runCount: number;
  estimatedCostUsd: number;
  unpricedRuns: number;
}

export interface DailyAICostEstimate {
  date: string;
  totalCalls: number;
  analyzedCalls: number;
  modelRuns: number;
  estimatedSpendUsd: number;
  averagePerAnalyzedCallUsd: number;
  unpricedRuns: number;
  stageRuns: Record<AIStageKey, number>;
}

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

function getRate(model: string | null): ModelRate | null {
  if (!model) return null;
  return MODEL_RATES.find((candidate) => candidate.match.test(model))?.rate ?? null;
}

function costFromProfile(stage: AIStageKey, model: string | null): number | null {
  const rate = getRate(model);
  if (!rate) return null;
  const profile = STAGE_TOKEN_PROFILE[stage];
  return (
    (profile.input / 1_000_000) * rate.inputPerMillionUsd +
    (profile.output / 1_000_000) * rate.outputPerMillionUsd
  );
}

export function formatModelName(model?: string | null): string {
  if (!model) return 'Model not persisted';
  if (/claude-sonnet-5/i.test(model)) return 'Claude Sonnet 5';
  if (/claude-opus-5/i.test(model)) return 'Claude Opus 5';
  if (/claude-haiku-4\.5/i.test(model)) return 'Claude Haiku 4.5';
  if (/gpt-5\.6-luna/i.test(model)) return 'GPT-5.6 Luna';
  return model.split('/').pop()?.replaceAll('-', ' ') || model;
}

function run(
  stage: AIStageKey,
  model: string | null | undefined,
  options?: { inferredModel?: string }
): AIRunEstimate {
  const modelInferred = !model && Boolean(options?.inferredModel);
  const resolvedModel = model || options?.inferredModel || null;
  const profile = STAGE_TOKEN_PROFILE[stage];
  return {
    stage,
    label: AI_STAGE_LABELS[stage],
    model: resolvedModel,
    modelLabel: formatModelName(resolvedModel),
    modelInferred,
    sampledInputTokens: profile.input,
    sampledOutputTokens: profile.output,
    estimatedCostUsd: costFromProfile(stage, resolvedModel),
  };
}

export function estimateCallAICost(call: AICallLike): AICallCostEstimate {
  const runs: AIRunEstimate[] = [];
  const transferCourtRan =
    call.qa_scored === true ||
    hasValue(call.prosecutor_case) ||
    hasValue(call.prosecutor_would_rule) ||
    hasValue(call.defender_case) ||
    hasValue(call.defender_would_rule) ||
    hasValue(call.supervisor_disposition);

  if (transferCourtRan) {
    runs.push(run('prosecutor', call.prosecutor_model));
    runs.push(run('defender', call.defender_model));
    runs.push(run('supervisor', call.supervisor_model));
  }

  const seniorReviewRan =
    call.sr_escalated === true ||
    hasValue(call.sr_disposition) ||
    call.court_tier_reached?.toLowerCase() === 'sr';
  if (seniorReviewRan) runs.push(run('senior_review', call.sr_model));

  const mvaRan =
    call.qa_scored === true || hasValue(call.mva_qualified) || hasValue(call.mva_evidence);
  if (mvaRan) runs.push(run('mva', call.mva_model));

  if (call.mva_escalated === true) {
    runs.push(
      run('mva_escalation', null, {
        // The workflow currently uses Sonnet for this conditional stage, but
        // its model ID is not yet persisted separately on the call row.
        inferredModel: 'anthropic/claude-sonnet-5',
      })
    );
  }

  const contactComplianceRan =
    hasValue(call.contact_compliance_assessment_status) ||
    hasValue(call.contact_compliance_scored_at);
  if (contactComplianceRan) {
    runs.push(run('contact_compliance', call.contact_compliance_model));
  }

  return {
    runs,
    runCount: runs.length,
    estimatedCostUsd: runs.reduce(
      (sum, item) => sum + (item.estimatedCostUsd ?? 0),
      0
    ),
    unpricedRuns: runs.filter((item) => item.estimatedCostUsd === null).length,
  };
}

function blankStageRuns(): Record<AIStageKey, number> {
  return {
    prosecutor: 0,
    defender: 0,
    supervisor: 0,
    senior_review: 0,
    mva: 0,
    mva_escalation: 0,
    contact_compliance: 0,
  };
}

export function buildDailyAICostEstimates(
  calls: AICallLike[],
  dates: string[]
): DailyAICostEstimate[] {
  const byDate = new Map<
    string,
    Omit<DailyAICostEstimate, 'date' | 'averagePerAnalyzedCallUsd'>
  >();

  for (const date of dates) {
    byDate.set(date, {
      totalCalls: 0,
      analyzedCalls: 0,
      modelRuns: 0,
      estimatedSpendUsd: 0,
      unpricedRuns: 0,
      stageRuns: blankStageRuns(),
    });
  }

  for (const call of calls) {
    const date = call.Date?.slice(0, 10);
    if (!date || !byDate.has(date)) continue;
    const current = byDate.get(date)!;
    const estimate = estimateCallAICost(call);
    current.totalCalls += 1;
    if (estimate.runCount > 0) current.analyzedCalls += 1;
    current.modelRuns += estimate.runCount;
    current.estimatedSpendUsd += estimate.estimatedCostUsd;
    current.unpricedRuns += estimate.unpricedRuns;
    for (const item of estimate.runs) current.stageRuns[item.stage] += 1;
  }

  return dates.map((date) => {
    const value = byDate.get(date)!;
    return {
      date,
      ...value,
      averagePerAnalyzedCallUsd:
        value.analyzedCalls > 0 ? value.estimatedSpendUsd / value.analyzedCalls : 0,
    };
  });
}

export function formatEstimatedCost(value: number): string {
  if (value === 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}
